/**
 *  @class
 *  @function RecentlyViewedDrawer
 */
if (!customElements.get('recently-viewed-drawer')) {
  class RecentlyViewedDrawer extends HTMLElement {
    constructor() {
      super();

      this.tab = this.querySelector('.recently-viewed__tab');
      this.overlay = this.querySelector('.recently-viewed__overlay');
      this.trackWrapper = this.querySelector('.recently-viewed__track-wrapper');
      this.track = this.querySelector('.recently-viewed__track');
      this.prevBtn = this.querySelector('.recently-viewed__nav--prev');
      this.nextBtn = this.querySelector('.recently-viewed__nav--next');

      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 20;
      this.perPageDesktop = parseInt(this.dataset.perPageDesktop, 10) || 4;
      this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 2;
      this.mobileMedia = window.matchMedia('(max-width: 767px)');
      this.cacheKey = 'recently-viewed-drawer-cache';
      this.cacheTtl = 10 * 60 * 1000;
      this.loadingDelay = 400;
      this.rootUrl = ((window.theme && theme.routes && theme.routes.root_url) || '/').replace(/\/$/, '');

      this.isOpen = false;
      this.currentPage = 0;
      this.totalPages = 0;
      this.products = [];
      this.cachedHandlesKey = '';
      this.fetchController = null;
      this.loadingTimer = null;
    }

    connectedCallback() {
      this._tabTouchHandled = false;

      this.tab.addEventListener('touchend', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._tabTouchHandled = true;
        this.toggle();
      });

      this.tab.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this._tabTouchHandled) {
          this._tabTouchHandled = false;
          return;
        }
        this.toggle();
      });
      this.overlay.addEventListener('click', this.close.bind(this));
      this.prevBtn.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      this.nextBtn.addEventListener('click', () => this.goToPage(this.currentPage + 1));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      document.addEventListener('recently-viewed:updated', () => {
        this.prefetchProducts(true);
      });

      this.updatePerPageVar();
      this.updateLayoutMode();
      this.initDragScroll();
      this.mobileMedia.addEventListener('change', this.handleResize.bind(this));
      window.addEventListener('resize', this.handleResize.bind(this));

      this.restoreFromSessionCache();
      this.schedulePrefetch();
    }

    schedulePrefetch() {
      const run = () => this.prefetchProducts(true);

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 800);
      }
    }

    get perPage() {
      return this.isMobile ? this.perPageMobile : this.perPageDesktop;
    }

    get isMobile() {
      return this.mobileMedia.matches;
    }

    getHandlesKey(handles) {
      return handles.join('|');
    }

    getRecentHandles() {
      let handles = [];

      if (window.localStorage) {
        const stored = window.localStorage.getItem('recently-viewed');
        if (stored && stored !== 'undefined') {
          try {
            handles = JSON.parse(stored);
          } catch (error) {
            handles = [];
          }
        }
      }

      if (window.recentlyViewedIds && window.recentlyViewedIds.length) {
        handles = window.recentlyViewedIds;
      }

      return handles.slice(0, this.maxProducts);
    }

    readSessionCache(handlesKey) {
      if (!window.sessionStorage) {
        return null;
      }

      try {
        const raw = window.sessionStorage.getItem(this.cacheKey);
        if (!raw) {
          return null;
        }

        const cache = JSON.parse(raw);
        if (!cache || cache.key !== handlesKey || !Array.isArray(cache.items)) {
          return null;
        }

        return cache;
      } catch (error) {
        return null;
      }
    }

    writeSessionCache(handlesKey, items) {
      if (!window.sessionStorage) {
        return;
      }

      try {
        window.sessionStorage.setItem(this.cacheKey, JSON.stringify({
          key: handlesKey,
          timestamp: Date.now(),
          items: items
        }));
      } catch (error) {
        /* ignore quota errors */
      }
    }

    restoreFromSessionCache() {
      const handles = this.getRecentHandles();
      if (!handles.length) {
        return;
      }

      const handlesKey = this.getHandlesKey(handles);
      const sessionCache = this.readSessionCache(handlesKey);

      if (sessionCache && sessionCache.items.length) {
        this.cachedHandlesKey = handlesKey;
        this.preloadImages(sessionCache.items);
        this.products = sessionCache.items.map((item) => this.buildProductElement(item));
      }
    }

    formatMoney(cents) {
      if (typeof formatMoney === 'function' && window.theme && theme.settings) {
        return formatMoney(cents, theme.settings.money_with_currency_format);
      }

      return (cents / 100).toFixed(2);
    }

    normalizeImageUrl(image) {
      if (!image) {
        return '';
      }

      let url = image;

      if (url.startsWith('//')) {
        url = 'https:' + url;
      }

      if (url.includes('cdn.shopify.com')) {
        url = url.split('?')[0] + '?width=300';
      }

      return url;
    }

    preloadImages(items) {
      items.forEach((item) => {
        if (!item.image) {
          return;
        }

        const img = new Image();
        img.decoding = 'async';
        img.src = item.image;
      });
    }

    buildProductElement(item) {
      const el = document.createElement('div');
      el.className = 'recently-viewed__product';
      el.dataset.handle = item.handle;

      const hasCompare = item.compare_at_price && item.compare_at_price > item.price;
      const priceHtml = hasCompare
        ? `<span class="recently-viewed__price-sale">${this.formatMoney(item.price)}</span><span class="recently-viewed__price-compare">${this.formatMoney(item.compare_at_price)}</span>`
        : `<span class="recently-viewed__price-sale">${this.formatMoney(item.price)}</span>`;

      const imageUrl = this.normalizeImageUrl(item.image);
      const imageHtml = imageUrl
        ? `<img class="recently-viewed__product-img" src="${imageUrl}" alt="${item.title.replace(/"/g, '&quot;')}" width="300" height="300" loading="eager" decoding="async">`
        : `<div class="recently-viewed__product-placeholder"></div>`;

      el.innerHTML = `
        <a href="${item.url}" class="recently-viewed__product-link" title="${item.title.replace(/"/g, '&quot;')}">
          <div class="recently-viewed__product-image">${imageHtml}</div>
          <p class="recently-viewed__product-title">${item.title}</p>
          <div class="recently-viewed__product-price">${priceHtml}</div>
        </a>
      `;

      return el;
    }

    async fetchProductData(handle) {
      const response = await fetch(`${this.rootUrl}/products/${handle}.js`, {
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Product not found');
      }

      const product = await response.json();

      return {
        handle: product.handle,
        title: product.title,
        url: product.url,
        image: this.normalizeImageUrl(product.featured_image),
        price: product.price,
        compare_at_price: product.compare_at_price
      };
    }

    async loadProducts(handles, signal) {
      const results = await Promise.allSettled(
        handles.map((handle) => this.fetchProductData(handle))
      );

      if (signal && signal.aborted) {
        return null;
      }

      const items = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          items.push({ handle: handles[index], data: result.value });
        }
      });

      items.sort((a, b) => handles.indexOf(a.handle) - handles.indexOf(b.handle));

      return items.map((entry) => entry.data);
    }

    prefetchProducts(silent) {
      const handles = this.getRecentHandles();
      if (!handles.length) {
        return;
      }

      const handlesKey = this.getHandlesKey(handles);
      const sessionCache = this.readSessionCache(handlesKey);

      if (sessionCache && sessionCache.items.length && this.cachedHandlesKey !== handlesKey) {
        this.cachedHandlesKey = handlesKey;
        this.preloadImages(sessionCache.items);
        this.products = sessionCache.items.map((item) => this.buildProductElement(item));
      }

      if (this.cachedHandlesKey === handlesKey && this.products.length && sessionCache && Date.now() - sessionCache.timestamp < this.cacheTtl) {
        return;
      }

      this.fetchProducts({ silent: true, handles, handlesKey });
    }

    fetchProducts(options) {
      const handles = options.handles || this.getRecentHandles();
      const handlesKey = options.handlesKey || this.getHandlesKey(handles);
      const silent = options.silent === true;
      const hasVisibleContent = this.products.length > 0;

      if (!handles.length) {
        this.clearLoadingTimer();
        this.setLoading(false);
        this.products = [];
        this.cachedHandlesKey = '';
        this.renderProducts();
        return;
      }

      if (this.fetchController) {
        this.fetchController.abort();
      }

      this.fetchController = new AbortController();
      const signal = this.fetchController.signal;

      if (!silent && !hasVisibleContent) {
        this.loadingTimer = setTimeout(() => {
          if (!signal.aborted && !this.products.length) {
            this.setLoading(true);
          }
        }, this.loadingDelay);
      }

      this.loadProducts(handles, signal)
        .then((items) => {
          if (!items || signal.aborted) {
            return;
          }

          this.cachedHandlesKey = handlesKey;
          this.preloadImages(items);
          this.products = items.map((item) => this.buildProductElement(item));
          this.writeSessionCache(handlesKey, items);

          if (this.isOpen || hasVisibleContent) {
            this.renderProducts();
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return;
          }

          console.error(error);

          if (!this.products.length) {
            this.renderProducts();
          }
        })
        .finally(() => {
          if (!signal.aborted) {
            this.clearLoadingTimer();
            this.setLoading(false);
          }
        });
    }

    clearLoadingTimer() {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
    }

    updateLayoutMode() {
      this.classList.toggle('recently-viewed--mobile', this.isMobile);
    }

    initDragScroll() {
      this._dragActive = false;
      this._dragMoved = false;
      this._dragStartX = 0;
      this._dragScrollLeft = 0;

      this.track.addEventListener('pointerdown', (event) => {
        if (!this.track.classList.contains('is-mobile-scroll') || !this.isOpen) {
          return;
        }

        if (event.pointerType === 'touch') {
          return;
        }

        this._dragActive = true;
        this._dragMoved = false;
        this._dragStartX = event.clientX;
        this._dragScrollLeft = this.track.scrollLeft;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      });

      this.track.addEventListener('pointermove', (event) => {
        if (!this._dragActive) {
          return;
        }

        const deltaX = event.clientX - this._dragStartX;

        if (Math.abs(deltaX) > 3) {
          this._dragMoved = true;
        }

        this.track.scrollLeft = this._dragScrollLeft - deltaX;
      });

      this.track.addEventListener('pointerup', (event) => {
        if (!this._dragActive) {
          return;
        }

        this._dragActive = false;
        this.track.classList.remove('is-dragging');

        if (this._dragMoved) {
          this.track.querySelectorAll('.recently-viewed__product-link').forEach((link) => {
            link.addEventListener('click', (clickEvent) => {
              clickEvent.preventDefault();
            }, { once: true });
          });
        }

        if (this.track.hasPointerCapture(event.pointerId)) {
          this.track.releasePointerCapture(event.pointerId);
        }
      });

      this.track.addEventListener('pointercancel', () => {
        this._dragActive = false;
        this.track.classList.remove('is-dragging');
      });
    }

    updatePerPageVar() {
      this.style.setProperty('--recently-viewed-per-page', this.perPage);
    }

    handleResize() {
      this.updateLayoutMode();
      this.updatePerPageVar();
      if (this.products.length) {
        this.renderProducts();
      }
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.updateLayoutMode();
      this.classList.add('is-open');
      this.tab.setAttribute('aria-expanded', 'true');

      const handles = this.getRecentHandles();
      const handlesKey = this.getHandlesKey(handles);

      if (!handles.length) {
        this.products = [];
        this.renderProducts();
        return;
      }

      if (this.cachedHandlesKey === handlesKey && this.products.length) {
        this.renderProducts();
        this.fetchProducts({ silent: true, handles, handlesKey });
        return;
      }

      const sessionCache = this.readSessionCache(handlesKey);
      if (sessionCache && sessionCache.items.length) {
        this.cachedHandlesKey = handlesKey;
        this.preloadImages(sessionCache.items);
        this.products = sessionCache.items.map((item) => this.buildProductElement(item));
        this.renderProducts();
        this.fetchProducts({ silent: true, handles, handlesKey });
        return;
      }

      this.fetchProducts({ silent: false, handles, handlesKey });
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.tab.setAttribute('aria-expanded', 'false');
    }

    renderProducts() {
      this.updatePerPageVar();
      this.track.innerHTML = '';

      if (!this.products.length) {
        this.classList.add('is-empty');
        this.totalPages = 0;
        this.updateNav();
        return;
      }

      this.classList.remove('is-empty');

      if (this.isMobile) {
        this.renderMobile();
      } else {
        this.renderDesktop();
      }
    }

    renderDesktop() {
      this.track.classList.remove('is-mobile-scroll');
      this.track.style.transform = '';

      const perPage = this.perPageDesktop;
      this.totalPages = Math.ceil(this.products.length / perPage);

      if (this.currentPage >= this.totalPages) {
        this.currentPage = this.totalPages - 1;
      }

      if (this.currentPage < 0) {
        this.currentPage = 0;
      }

      for (let pageIndex = 0; pageIndex < this.totalPages; pageIndex += 1) {
        const page = document.createElement('div');
        page.className = 'recently-viewed__page';

        const start = pageIndex * perPage;
        const end = start + perPage;

        this.products.slice(start, end).forEach((product) => {
          page.appendChild(product.cloneNode(true));
        });

        this.track.appendChild(page);
      }

      this.updateDesktopTransform();
      this.updateNav();
    }

    renderMobile() {
      this.track.classList.add('is-mobile-scroll');
      this.track.style.transform = '';
      this.track.scrollLeft = 0;
      this.totalPages = 1;

      this.products.forEach((product) => {
        this.track.appendChild(product.cloneNode(true));
      });

      this.updateNav();
    }

    updateDesktopTransform() {
      this.track.style.transform = 'translateX(-' + (this.currentPage * 100) + '%)';
    }

    goToPage(page) {
      if (page < 0 || page >= this.totalPages) {
        return;
      }

      this.currentPage = page;
      this.updateDesktopTransform();
      this.updateNav();
    }

    updateNav() {
      if (this.isMobile) {
        this.prevBtn.classList.add('is-hidden');
        this.nextBtn.classList.add('is-hidden');
        return;
      }

      if (this.totalPages <= 1) {
        this.prevBtn.classList.add('is-hidden');
        this.nextBtn.classList.add('is-hidden');
        return;
      }

      if (this.currentPage <= 0) {
        this.prevBtn.classList.add('is-hidden');
      } else {
        this.prevBtn.classList.remove('is-hidden');
      }

      if (this.currentPage >= this.totalPages - 1) {
        this.nextBtn.classList.add('is-hidden');
      } else {
        this.nextBtn.classList.remove('is-hidden');
      }
    }

  }

  customElements.define('recently-viewed-drawer', RecentlyViewedDrawer);
}
